import {
  BasicType,
  BlockManager,
  IPage,
  IBlockData,
} from "easy-email-core";

import type { EmailBuilderTemplate } from "./index";

function block<T extends IBlockData>(type: string, payload: any = {}): T {
  const created = BlockManager.getBlockByType(type)?.create(payload);
  if (!created) {
    throw new Error(`easy-email block type "${type}" is not registered`);
  }
  return created as T;
}

function buildPage(children: IBlockData[], pageOverrides: any = {}): IPage {
  const page = block<IPage>(BasicType.PAGE, pageOverrides);
  page.children = children;
  return page;
}

function section(columns: IBlockData[], attrs: any = {}): IBlockData {
  return block(BasicType.SECTION, {
    attributes: { padding: "16px 24px", ...attrs },
    children: columns,
  });
}

function column(children: IBlockData[], attrs: any = {}): IBlockData {
  return block(BasicType.COLUMN, {
    attributes: { width: "100%", ...attrs },
    children,
  });
}

function text(content: string, attrs: any = {}): IBlockData {
  return block(BasicType.TEXT, {
    data: { value: { content } },
    attributes: { padding: "8px 24px", ...attrs },
  });
}

function image(src: string, attrs: any = {}): IBlockData {
  return block(BasicType.IMAGE, {
    attributes: { src, padding: "16px 24px", ...attrs },
  });
}

function button(content: string, href: string, attrs: any = {}): IBlockData {
  return block(BasicType.BUTTON, {
    data: { value: { content } },
    attributes: {
      href,
      "background-color": "#1976d2",
      color: "#ffffff",
      "border-radius": "6px",
      "font-weight": "600",
      padding: "16px 24px",
      "inner-padding": "12px 28px",
      align: "center",
      ...attrs,
    },
  });
}

function divider(attrs: any = {}): IBlockData {
  return block(BasicType.DIVIDER, {
    attributes: { padding: "8px 24px", "border-color": "#e0e0e0", ...attrs },
  });
}

function spacer(height = "24px"): IBlockData {
  return block(BasicType.SPACER, { attributes: { height } });
}

function makeWelcome(): EmailBuilderTemplate {
  return {
    subject: "Welcome to {{companyName}}",
    subTitle: "Thanks for joining — here's what's next",
    content: buildPage([
      section([
        column([
          text(
            '<h1 style="margin:0;font-size:28px;line-height:1.2;">Welcome, {{firstName}}!</h1>',
            { padding: "32px 24px 8px" }
          ),
          text(
            "We're glad to have you here. Take a minute to set up your account and explore.",
            { padding: "8px 24px 24px" }
          ),
          button("Get started", "https://example.com/get-started"),
          spacer(),
          divider(),
          text(
            '<p style="margin:0;color:#6b7280;font-size:13px;">Questions? Just reply to this email — a real person will get back to you.</p>',
            { padding: "16px 24px 32px" }
          ),
        ]),
      ]),
    ]),
  };
}

function makeNewsletter(): EmailBuilderTemplate {
  return {
    subject: "{{companyName}} — what's new this week",
    subTitle: "The latest updates and reads",
    content: buildPage([
      section([
        column([
          text(
            '<h1 style="margin:0;font-size:24px;">This week at {{companyName}}</h1>',
            { padding: "32px 24px 0" }
          ),
          text(
            '<p style="margin:0;color:#6b7280;">Hi {{firstName}}, here are a few things worth your attention.</p>',
            { padding: "8px 24px 16px" }
          ),
        ]),
      ]),
      section([
        column([
          image("https://placehold.co/600x300/EEE/777?text=Lead+story"),
          text(
            '<h2 style="margin:0;font-size:20px;">Lead story headline</h2>',
            { padding: "8px 24px 0" }
          ),
          text(
            "Short, scannable summary of the lead story. Two sentences is plenty — link out for the rest.",
            { padding: "8px 24px 16px" }
          ),
          button("Read more", "https://example.com/article-1", {
            "background-color": "#111827",
          }),
        ]),
      ]),
      divider(),
      section(
        [
          column(
            [
              text(
                '<h3 style="margin:0;font-size:16px;">Quick read</h3>',
                { padding: "8px 24px 0" }
              ),
              text(
                "A second story — kept tight so the email scans well on mobile.",
                { padding: "4px 24px 8px" }
              ),
            ],
            { width: "50%" }
          ),
          column(
            [
              text(
                '<h3 style="margin:0;font-size:16px;">Heads up</h3>',
                { padding: "8px 24px 0" }
              ),
              text(
                "A third story or announcement. Keep the design balanced.",
                { padding: "4px 24px 8px" }
              ),
            ],
            { width: "50%" }
          ),
        ],
        { padding: "8px 0" }
      ),
    ]),
  };
}

function makePromo(): EmailBuilderTemplate {
  return {
    subject: "A little something for you, {{firstName}}",
    subTitle: "Limited-time offer inside",
    content: buildPage(
      [
        section([
          column([
            text(
              '<h1 style="margin:0;font-size:32px;text-align:center;line-height:1.1;">20% off, just for you</h1>',
              { padding: "40px 24px 8px" }
            ),
            text(
              '<p style="margin:0;text-align:center;color:#374151;">Use code <strong>WELCOME20</strong> at checkout. Valid for the next 7 days.</p>',
              { padding: "8px 24px 24px" }
            ),
            button("Shop now", "https://example.com/shop"),
            spacer(),
          ]),
        ]),
      ],
      {
        attributes: { "background-color": "#fef3c7" },
        data: { value: { "content-background-color": "#ffffff" } },
      }
    ),
  };
}

function makePlainAnnouncement(): EmailBuilderTemplate {
  return {
    subject: "Quick update from {{companyName}}",
    subTitle: "",
    content: buildPage([
      section([
        column([
          text("Hi {{firstName}},", { padding: "32px 24px 8px" }),
          text(
            "Wanted to share a quick update with you. Replace this paragraph with whatever you need to say — keep it short and the response rates stay high.",
            { padding: "8px 24px" }
          ),
          text(
            "If you have questions, just reply.",
            { padding: "8px 24px" }
          ),
          text(
            '<p style="margin:24px 0 0;">— The {{companyName}} team</p>',
            { padding: "8px 24px 32px" }
          ),
        ]),
      ]),
    ]),
  };
}

export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  build: () => EmailBuilderTemplate;
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: "welcome",
    name: "Welcome",
    description: "Greet a new contact and point them to the next step",
    build: makeWelcome,
  },
  {
    id: "newsletter",
    name: "Newsletter",
    description: "Lead story with two short follow-ups",
    build: makeNewsletter,
  },
  {
    id: "promo",
    name: "Promotional offer",
    description: "Single-call-to-action promo with a discount code",
    build: makePromo,
  },
  {
    id: "plain",
    name: "Plain announcement",
    description: "A short text-only update — high response rates",
    build: makePlainAnnouncement,
  },
];
